import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

const EMP_STATUS = [
  { v: "ativo",     l: "Ativo" },
  { v: "afastado",  l: "Afastado" },
  { v: "inativo",   l: "Inativo" },
  { v: "desligado", l: "Desligado" },
];
const TURNOS = ["Diurno", "Noturno", "Comercial", "Escala 12x36", "Escala 6x1"];

export default function Employees() {
  const [searchParams] = useSearchParams();
  const initialCompany = searchParams.get("empresa") || "";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(initialCompany);
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      if (!companyId && r.data.length) setCompanyId(r.data[0].id);
    });
    // eslint-disable-next-line
  }, []);

  const load = () => {
    if (!companyId) return;
    const params = { company_id: companyId };
    if (q) params.q = q;
    api.get("/employees", { params }).then((r) => setEmployees(r.data));
  };
  useEffect(load, [companyId, q]);  // eslint-disable-line

  const openNew = () => { setEditing(null); setShowForm(true); };
  const openEdit = (e) => { setEditing(e); setShowForm(true); };
  const onSaved = () => { setShowForm(false); load(); };
  const onDelete = async (e) => {
    if (!window.confirm(`Excluir ${e.nome}? Os atestados ficam preservados.`)) return;
    try {
      await api.delete(`/employees/${e.id}`);
      toast.success("Colaborador removido.");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline">Absenteísmo</div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 mt-1">Colaboradores</h1>
          <Link to="/app/absenteismo" className="text-sm text-emerald-800 hover:underline mt-2 inline-block">
            <i className="fa-solid fa-arrow-left mr-2"></i>Voltar ao painel
          </Link>
        </div>
        <button onClick={openNew} className="btn btn-primary" data-testid="employee-new-btn">
          <i className="fa-solid fa-plus"></i> Novo colaborador
        </button>
      </header>

      <div className="card flex flex-wrap items-end gap-3">
        <label className="flex-1 min-w-[220px]">
          <div className="overline mb-1">Empresa</div>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}
                  className="w-full border border-slate-300 px-3 py-2">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.razao_social || c.nome_fantasia}</option>)}
          </select>
        </label>
        <label className="flex-1 min-w-[220px]">
          <div className="overline mb-1">Buscar (nome, matrícula ou CPF)</div>
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 data-testid="employee-search"
                 className="w-full border border-slate-300 px-3 py-2" placeholder="Ex: João" />
        </label>
        <div className="text-sm text-slate-500">
          <strong>{employees.length}</strong> colaborador(es)
        </div>
      </div>

      <div className="card overflow-x-auto" data-testid="employees-table">
        {employees.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <i className="fa-solid fa-users text-4xl mb-3"></i>
            <div className="text-sm">Nenhum colaborador cadastrado.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b">
                <th className="text-left py-2">Nome</th>
                <th className="text-left">Matrícula</th>
                <th className="text-left">Cargo</th>
                <th className="text-left">Setor</th>
                <th className="text-left">GHE</th>
                <th className="text-left">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e) => (
                <tr key={e.id} className="border-b border-slate-100" data-testid={`employee-row-${e.id}`}>
                  <td className="py-2 font-medium">{e.nome}</td>
                  <td className="text-slate-600">{e.matricula || "—"}</td>
                  <td>{e.cargo || "—"}</td>
                  <td>{e.setor || "—"}</td>
                  <td>{e.ghe || "—"}</td>
                  <td>
                    <span className={`text-xs px-2 py-0.5 ${
                      e.status === "ativo" ? "bg-emerald-100 text-emerald-900"
                      : e.status === "afastado" ? "bg-amber-100 text-amber-900"
                      : "bg-slate-100 text-slate-600"}`}>
                      {EMP_STATUS.find((s) => s.v === e.status)?.l || e.status}
                    </span>
                  </td>
                  <td className="text-right">
                    <button onClick={() => openEdit(e)} className="text-emerald-800 hover:underline text-xs mr-3"
                            data-testid={`employee-edit-${e.id}`}>Editar</button>
                    <button onClick={() => onDelete(e)} className="text-rose-700 hover:underline text-xs">Excluir</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && <EmployeeForm companyId={companyId} editing={editing} onClose={() => setShowForm(false)} onSaved={onSaved} />}
    </div>
  );
}

function EmployeeForm({ companyId, editing, onClose, onSaved }) {
  const [form, setForm] = useState(() => editing || {
    company_id: companyId,
    nome: "", matricula: "", cpf: "", cargo: "", setor: "", ghe: "",
    unidade: "", turno: "", data_admissao: "", status: "ativo", email: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.nome.trim()) { toast.error("Nome é obrigatório."); return; }
    setBusy(true);
    try {
      const payload = { ...form, company_id: companyId };
      if (editing) await api.put(`/employees/${editing.id}`, payload);
      else         await api.post(`/employees`, payload);
      toast.success(editing ? "Colaborador atualizado." : "Colaborador cadastrado.");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="employee-form-modal">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <h2 className="font-display text-xl font-extrabold">{editing ? "Editar colaborador" : "Novo colaborador"}</h2>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-3 text-sm">
          {[
            ["nome", "Nome completo *", "text"],
            ["matricula", "Matrícula", "text"],
            ["cpf", "CPF", "text"],
            ["email", "E-mail", "email"],
            ["cargo", "Cargo", "text"],
            ["setor", "Setor", "text"],
            ["ghe", "GHE", "text"],
            ["unidade", "Unidade", "text"],
            ["data_admissao", "Data de admissão", "date"],
          ].map(([k, label, type]) => (
            <label key={k} className="block">
              <div className="overline mb-1">{label}</div>
              <input type={type} value={form[k] || ""} onChange={set(k)}
                     data-testid={`emp-field-${k}`}
                     className="w-full border border-slate-300 px-3 py-2" />
            </label>
          ))}
          <label className="block">
            <div className="overline mb-1">Turno</div>
            <select value={form.turno || ""} onChange={set("turno")} data-testid="emp-field-turno"
                    className="w-full border border-slate-300 px-3 py-2">
              <option value="">—</option>
              {TURNOS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="overline mb-1">Status</div>
            <select value={form.status || "ativo"} onChange={set("status")} data-testid="emp-field-status"
                    className="w-full border border-slate-300 px-3 py-2">
              {EMP_STATUS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </label>
        </div>
        <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
          <button onClick={onClose} className="btn btn-ghost border border-slate-300">Cancelar</button>
          <button onClick={save} disabled={busy} data-testid="employee-save-btn"
                  className="btn btn-primary disabled:opacity-50">
            {busy ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}
